import { Box, Stack, Typography } from "@mui/material";
import { TrendingUp, TrendingDown, TrendingFlat } from "@mui/icons-material";
import { LightTooltip } from "@/components/LightTooltip";
import { getTrendDirection, getTrendDescription } from "./utils/formatters";

interface TrendIconProps {
  /** Trend percentage value (e.g., 15.5 = 15.5% increase) */
  trend: number;
  /** Topic display name for tooltip context */
  topicName: string;
}

/**
 * Displays a trend indicator icon with tooltip
 * - Green up arrow for > 10% increase
 * - Red down arrow for < -10% decrease
 * - Gray flat arrow for -10% to +10%
 */
export const TrendIcon = ({ trend, topicName }: TrendIconProps) => {
  const direction = getTrendDirection(trend);
  const description = getTrendDescription(trend, topicName);

  const iconProps = {
    fontSize: "small" as const,
    sx: { ml: 0.5 },
  };

  const icon = {
    up: (
      <TrendingUp
        {...iconProps}
        sx={{ ...iconProps.sx, color: "success.main" }}
      />
    ),
    down: (
      <TrendingDown
        {...iconProps}
        sx={{ ...iconProps.sx, color: "error.main" }}
      />
    ),
    flat: (
      <TrendingFlat
        {...iconProps}
        sx={{ ...iconProps.sx, color: "grey.500" }}
      />
    ),
  }[direction];

  const tooltipContent = (
    <Stack spacing={1}>
      {/* Section 1: Topic Trends explanation */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Topic Trends
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Trends show whether activity for a topic has been growing or weakening
          over time
        </Typography>
      </Box>

      {/* Section 2: Specific topic trend */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {topicName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <LightTooltip title={tooltipContent} placement="top">
      {icon}
    </LightTooltip>
  );
};
